import TermsHistory from "@/components/TermsHistory";
import TermsUpdate from "@/components/TermsUpdate";
import AuthoredMessages from "@/components/AuthoredMessages";
import { useProfile } from "@/contexts/ProfileContext";
import notice from "@/lib/consentNotice.json";
import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Compass, Heart, Sparkles, MessageCircle, User, Bell, Settings as SettingsIcon, LogOut, Crown } from "lucide-react";
import { useI18n } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import RetentionNotices from "@/components/RetentionNotices";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const NAV = [
  { key: "discover", to: "/app/discover", icon: Compass },
  { key: "matches", to: "/app/matches", icon: Sparkles },
  { key: "messages", to: "/app/messages", icon: MessageCircle },
  { key: "likes", to: "/app/likes", icon: Heart },
  { key: "notifications", to: "/app/notifications", icon: Bell },
];
const NAV_EXTRA = [
  { key: "profile", to: "/app/profile", icon: User },
  { key: "settings", to: "/app/settings", icon: SettingsIcon },
];

export default function AppShell() {
  const { t, lang } = useI18n();
  const { user, logout } = useAuth();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  // Refresh badges after reading messages, on route change, and every 15s.
  useEffect(() => {
    let cancelled = false;
    let requestId = 0;
    const fetchAll = async () => {
      const currentRequest = ++requestId;
      try {
        const [notifRes, convRes] = await Promise.allSettled([
          api.get("/notifications/unread-count"),
          api.get("/conversations"),
        ]);
        if (cancelled || currentRequest !== requestId) return;
        if (notifRes.status === "fulfilled") {
          setUnread(notifRes.value.data.unread_count || 0);
        }
        if (convRes.status === "fulfilled") {
          setUnreadMessages(convRes.value.data.total_unread || 0);
        }
      } catch {}
    };
    window.addEventListener("novaxlove:messages-read", fetchAll);
    fetchAll();
    const id = setInterval(fetchAll, 15000);
    return () => {
      cancelled = true;
      window.removeEventListener("novaxlove:messages-read", fetchAll);
      clearInterval(id);
    };
  }, [location.pathname]);

  const membershipLabel = lang === "it" ? "La mia iscrizione" : "My membership";

  return (
    <div className="min-h-screen flex" data-testid="app-shell">
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex md:w-72 shrink-0 flex-col border-r"
        style={{
          borderColor: "hsl(var(--nvl-border))",
          backgroundColor: "hsl(var(--nvl-surface))",
        }}
        data-testid="app-sidebar"
      >
        <Link to="/" className="p-6 border-b" style={{ borderColor: "hsl(var(--nvl-border))" }}>
          <span className="font-display text-xl tracking-tight">NO VAX LOVE</span>
        </Link>

        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            const notifCount = item.key === "notifications" ? unread : 0;
            const msgCount = item.key === "messages" ? unreadMessages : 0;
            const badgeCount = notifCount + msgCount;
            const showBadge = badgeCount > 0;
            return (
              <NavLink
                key={item.key}
                to={item.to}
                data-testid={`app-nav-${item.key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium ${
                    isActive ? "bg-[hsl(var(--nvl-ink))] text-[hsl(var(--nvl-bg))]" : "hover:bg-[hsl(var(--muted))]"
                  }`
                }
              >
                <Icon size={16} />
                <span className="flex-1">{t(`app.nav.${item.key}`)}</span>
                {showBadge ? (
                  <span
                    className="ml-auto text-[10px] px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: "hsl(var(--nvl-accent))", color: "hsl(var(--nvl-bg))" }}
                    data-testid={`app-nav-${item.key}-badge`}
                  >
                    {badgeCount > 9 ? "9+" : badgeCount}
                  </span>
                ) : null}
              </NavLink>
            );
          })}
          {NAV_EXTRA.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.key}
                to={item.to}
                data-testid={`app-nav-${item.key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium ${
                    isActive ? "bg-[hsl(var(--nvl-ink))] text-[hsl(var(--nvl-bg))]" : "hover:bg-[hsl(var(--muted))]"
                  }`
                }
              >
                <Icon size={16} />
                <span>{t(`app.nav.${item.key}`)}</span>
              </NavLink>
            );
          })}

          <NavLink
            to="/app/membership"
            data-testid="app-nav-membership"
            className={({ isActive }) =>
              `mt-3 flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium border ${
                isActive
                  ? "bg-[hsl(var(--nvl-accent))] text-[hsl(var(--nvl-bg))] border-transparent"
                  : "hover:bg-[hsl(var(--muted))]"
              }`
            }
            style={{ borderColor: "hsl(var(--nvl-accent))" }}
          >
            <Crown size={16} />
            <span>{membershipLabel}</span>
          </NavLink>
        </nav>

        <div className="p-4 border-t" style={{ borderColor: "hsl(var(--nvl-border))" }}>
          <div className="mb-3 flex items-center justify-between text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            <span className="truncate max-w-[160px]" data-testid="app-user-email">
              {user?.email}
            </span>
            <LanguageSwitcher compact />
          </div>
          <button
            onClick={handleLogout}
            data-testid="app-logout-button"
            className="w-full inline-flex items-center gap-2 justify-center rounded-md border py-2 text-sm hover:bg-[hsl(var(--muted))]"
            style={{ borderColor: "hsl(var(--nvl-border))" }}
          >
            <LogOut size={14} /> {t("common.signOut")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0" data-testid="app-main">
        {/* Mobile top bar */}
        <div
          className="md:hidden sticky top-0 z-30 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-2 border-b"
          style={{
            borderColor: "hsl(var(--nvl-border))",
            backgroundColor: "hsl(var(--nvl-bg) / 0.9)",
            backdropFilter: "blur(12px)",
          }}
        >
          <Link to="/app/profile" className="font-display text-lg tracking-tight">
            NO VAX LOVE
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <LanguageSwitcher compact />
            <NavLink
              to="/app/membership"
              className="text-xs underline"
              style={{ color: "hsl(var(--nvl-accent))" }}
              data-testid="app-nav-membership-mobile"
            >
              {membershipLabel}
            </NavLink>
            <button
              onClick={handleLogout}
              data-testid="app-logout-button-mobile"
              className="text-xs underline"
              style={{ color: "hsl(var(--nvl-ink-soft))" }}
            >
              {t("common.signOut")}
            </button>
          </div>
        </div>

        <div className="flex-1 min-w-0 p-4 sm:p-6 md:p-10 pb-[calc(9rem+env(safe-area-inset-bottom))] md:pb-10">
          {profile && !location.pathname.startsWith("/app/messages/") && Object.keys(notice.groups).some(group => profile.sensitive_consents?.[group]?.version !== notice.version) && (
            <div className="nvl-card p-4 mb-5 text-sm" data-testid="consent-review-notice">
              {lang === "it" ? "Scegli come usare i dati delicati del profilo. Finché non scegli, non li utilizziamo nella ricerca né li mostriamo agli altri. " : "Choose how your sensitive profile data is used. Until you choose, it is not used in search or shown to others. "}
              <Link className="underline" to="/app/settings/privacy">{lang === "it" ? "Gestisci le scelte" : "Manage choices"}</Link>
            </div>
          )}
          {location.pathname !== "/app/settings/privacy" && <RetentionNotices compact key={location.pathname} />}
          <TermsUpdate />
          {location.pathname === "/app/settings/privacy" && <><TermsHistory /><AuthoredMessages /></>}
          <Outlet />
        </div>

        {/* Mobile bottom nav */}
        <nav
          className="md:hidden fixed bottom-0 left-0 right-0 z-30 border-t"
          style={{
            borderColor: "hsl(var(--nvl-border))",
            backgroundColor: "hsl(var(--nvl-bg) / 0.95)",
            backdropFilter: "blur(12px)",
          }}
          data-testid="app-bottom-nav"
        >
          <div className="grid grid-cols-4 pb-[env(safe-area-inset-bottom)]">
            {[NAV_EXTRA[0], ...NAV, NAV_EXTRA[1]].map((item) => {
              const Icon = item.icon;
              const badgeVal = (item.key === "notifications" ? unread : 0) + (item.key === "messages" ? unreadMessages : 0);
              const showBadge = badgeVal > 0;
              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  data-testid={`app-nav-mobile-${item.key}`}
                  className={({ isActive }) =>
                    `relative flex min-w-0 min-h-[56px] flex-col items-center justify-center px-1 py-2 text-[11px] leading-tight text-center ${
                      isActive ? "text-[hsl(var(--nvl-accent))]" : "text-[hsl(var(--nvl-ink-soft))]"
                    }`
                  }
                >
                  <Icon size={18} />
                  <span className="mt-1 break-words max-w-full">{t(`app.nav.${item.key}`)}</span>
                  {showBadge ? (
                    <span
                      className="absolute top-2 right-[calc(50%-18px)] w-2 h-2 rounded-full"
                      style={{ backgroundColor: "hsl(var(--nvl-accent))" }}
                    />
                  ) : null}
                </NavLink>
              );
            })}
          </div>
        </nav>
      </main>
    </div>
  );
}
